/* TODO(ASIMO-2105): cherrera2@ to remove google drive code once the experiment is over regardless of outcome */
import React, { useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { type AnalyticsEventPayload, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Grid, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	ANALYTICS_CHANNEL,
	buildAuthClickedEventPayload,
	buildAuthCompletedEventPayload,
	buildAuthFailedEventPayload,
	ThirdPartyAuthSource,
	withAnalyticsContext,
} from '../../services/analytics';
import {
	checkIfUserHasAuthorizedGoogleDriveConnector,
	checkIfUserHasAuthorizedOneDriveConnector,
} from '../../services/connectedDataSource';
import { useBrowserStorage } from '../../services/hooks';
import {
	DEFAULT_EXPIRATION_FOR_3P_CONNECTION_STATUS,
	getGoogleDriveConnectionBannerStatus,
	getGoogleDriveConnectionStatus,
	getOneDriveConnectionBannerStatus,
	getOneDriveConnectionStatus,
	GOOGLE_DRIVE_CONNECTION_BANNER_STORAGE_KEY,
	GOOGLE_DRIVE_CONNECTION_STATUS_STORAGE_KEY,
	ONE_DRIVE_CONNECTION_BANNER_STORAGE_KEY,
	ONE_DRIVE_CONNECTION_STATUS_STORAGE_KEY,
	type ThirdPartyBannerStatus,
	type ThirdPartyConnectionStatus,
} from '../../services/storage';
import {
	THIRD_PARTY_OAUTH_SUCCESS_EVENT,
	triggerThirdPartyAuthFlow,
} from '../../services/thirdPartyAuth';

export const GOOGLE_DRIVE_CONNECT_BANNER_CLOSE_BUTTON_TEST_ID =
	'google-drive-connect-banner-close-button';

export const messages = defineMessages({
	thirdPartyConnectBannerLink: {
		id: 'fabric.linkPickerAtlassianPlugin.thirdPartyConnectBannerLink',
		defaultMessage: 'Connect your apps to see more links',
		description: 'Link message shown in the third party connect banner',
	},
	thirdPartyConnectBannerClose: {
		id: 'fabric.linkPickerAtlassianPlugin.thirdPartyConnectBannerClose',
		defaultMessage: 'Dismiss',
		description: 'Close button message shown in the third party connect banner',
	},
});

const styles = cssMap({
	containerBaseStyles: {
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral', '#F5F5F5'),
		gridTemplateColumns: 'min-content auto 1fr auto',
		marginTop: token('space.100', '8px'),
		paddingTop: token('space.150', '12px'),
		paddingRight: token('space.150', '12px'),
		paddingBottom: token('space.150', '12px'),
		paddingLeft: token('space.150', '12px'),
	},
	pressableInlineStyles: {
		paddingLeft: token('space.100', '8px'),
	},
	pressableStyles: {
		color: token('color.link', '#0052CC'),
		font: token('font.body.small', '14px'),
		fontWeight: token('font.weight.medium', '500'),
		padding: '0',
	},
	closeButtonContainer: {
		justifySelf: 'end',
	},
});

const GoogleDriveIcon = () => {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="#FFFFFF"
			xmlns="http://www.w3.org/2000/svg"
		>
			<rect
				x="0.254162"
				y="0.254162"
				width="15.4917"
				height="15.4917"
				rx="3.74584"
				stroke="#0B1228"
				strokeOpacity="0.14"
				strokeWidth="0.508324"
			/>
			<g clipPath="url(#clip0_303_5302)">
				<path
					d="M3.4728 11.3855L3.9432 12.1939C4.04095 12.3641 4.18146 12.4978 4.34641 12.595L6.02644 9.70197H2.66638C2.66638 9.89038 2.71526 10.0788 2.813 10.249L3.4728 11.3855Z"
					fill="#0066DA"
				/>
				<path
					d="M7.99972 6.29837L6.31968 3.4053C6.15474 3.50255 6.01422 3.63626 5.91648 3.80644L2.813 9.15497C2.71705 9.32148 2.66651 9.51005 2.66638 9.70198H6.02644L7.99972 6.29837Z"
					fill="#00AC47"
				/>
				<path
					d="M11.6534 12.595C11.8183 12.4978 11.9588 12.3641 12.0566 12.1939L12.2521 11.8596L13.1868 10.249C13.2845 10.0788 13.3334 9.89038 13.3334 9.70197H9.97308L10.6881 11.0999L11.6534 12.595Z"
					fill="#EA4335"
				/>
				<path
					d="M7.99998 6.29846L9.68001 3.40539C9.51506 3.30814 9.32567 3.25952 9.13018 3.25952H6.86977C6.67428 3.25952 6.48489 3.31422 6.31995 3.40539L7.99998 6.29846Z"
					fill="#00832D"
				/>
				<path
					d="M9.97271 9.70197H6.02616L4.34613 12.595C4.51108 12.6923 4.70046 12.7409 4.89596 12.7409H11.1029C11.2984 12.7409 11.4878 12.6862 11.6527 12.595L9.97271 9.70197Z"
					fill="#2684FC"
				/>
				<path
					d="M11.6352 6.48071L10.0834 3.80644C9.98567 3.63626 9.84516 3.50255 9.68021 3.4053L8.00018 6.29837L9.97346 9.70198H13.3274C13.3274 9.51357 13.2785 9.32515 13.1808 9.15497L11.6352 6.48071Z"
					fill="#FFBA00"
				/>
			</g>
			<defs>
				<clipPath id="clip0_303_5302">
					<rect
						width="10.6667"
						height="9.48148"
						fill="white"
						transform="translate(2.6662 3.2594)"
					/>
				</clipPath>
			</defs>
		</svg>
	);
};

const OneDriveIcon = () => {
	return (
		<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
			<g clipPath="url(#clip0_1_445)">
				<path
					d="M6.57592 6.13485L6.57604 6.13444L9.09516 7.64336L10.5963 7.01167C10.9013 6.8798 11.2302 6.81218 11.5625 6.8125C11.6179 6.8125 11.6726 6.81502 11.727 6.81865C11.5466 6.11493 11.1656 5.47878 10.6304 4.9875C10.0952 4.49622 9.42882 4.17097 8.71226 4.05126C7.9957 3.93156 7.25979 4.02256 6.59397 4.3132C5.92815 4.60385 5.36108 5.08162 4.96167 5.68847C4.9745 5.68831 4.98714 5.6875 5 5.6875C5.55675 5.68675 6.10261 5.8417 6.57592 6.13485Z"
					fill="#0364B8"
				/>
				<path
					d="M6.57603 6.13444L6.57592 6.13485C6.1026 5.8417 5.55674 5.68675 5 5.68751C4.98714 5.68751 4.97448 5.68831 4.96166 5.68847C4.41675 5.69522 3.88398 5.85033 3.4206 6.13712C2.95722 6.42392 2.58074 6.83156 2.33164 7.31625C2.08253 7.80094 1.97021 8.34434 2.00675 8.88807C2.04328 9.4318 2.22729 9.95529 2.53899 10.4023L4.76049 9.46745L5.74803 9.05188L7.94685 8.12658L9.09515 7.64336L6.57603 6.13444Z"
					fill="#0078D4"
				/>
				<path
					d="M11.727 6.81865C11.6726 6.81502 11.6179 6.8125 11.5625 6.8125C11.2302 6.81218 10.9013 6.88005 10.5963 7.01192L9.09515 7.64336L9.53044 7.90409L10.9573 8.75876L11.5798 9.13165L13.7084 10.4067C13.9019 10.0476 14.0021 9.64577 14 9.23793C13.9978 8.8301 13.8934 8.42932 13.6963 8.0723C13.4991 7.71528 13.2155 7.41344 12.8715 7.19442C12.5274 6.9754 12.134 6.84619 11.727 6.81865Z"
					fill="#1490DF"
				/>
				<path
					d="M11.5798 9.13164L10.9573 8.75875L9.53045 7.90408L9.09516 7.64335L7.94686 8.12657L5.74803 9.05187L4.7605 9.46744L2.539 10.4023C2.81506 10.7992 3.18308 11.1234 3.61163 11.3472C4.04018 11.571 4.51653 11.6877 5 11.6875H11.5625C12.0025 11.6876 12.4343 11.5686 12.8122 11.3431C13.19 11.1176 13.4997 10.794 13.7085 10.4067L11.5798 9.13164Z"
					fill="#28A8EA"
				/>
			</g>
			<defs>
				<clipPath id="clip0_1_445">
					<rect width="12" height="12" fill="white" transform="translate(2 2)" />
				</clipPath>
			</defs>
		</svg>
	);
};

enum ThirdPartyConnectBannerSource {
	GOOGLE_DRIVE = 'google_drive',
	ONE_DRIVE = 'onedrive',
}

interface ThirdPartySourceMapping {
	bannerStatusStorageKey: string;
	connectionStatusStorageKey: string;
	connectorCheckMethod: (cloudId: string) => Promise<boolean>;
	fetchBannerStatusFromLocalStorage: () => ThirdPartyBannerStatus | null;
	fetchConnectionStatusFromLocalStorage: () => ThirdPartyConnectionStatus | null;
	icon: React.ReactNode;
	tab: string;
}

const ThirdPartySourceMapping: Record<ThirdPartyConnectBannerSource, ThirdPartySourceMapping> = {
	[ThirdPartyConnectBannerSource.GOOGLE_DRIVE]: {
		fetchBannerStatusFromLocalStorage: getGoogleDriveConnectionBannerStatus,
		bannerStatusStorageKey: GOOGLE_DRIVE_CONNECTION_BANNER_STORAGE_KEY,
		connectorCheckMethod: (cloudId: string) =>
			checkIfUserHasAuthorizedGoogleDriveConnector(cloudId),
		fetchConnectionStatusFromLocalStorage: getGoogleDriveConnectionStatus,
		connectionStatusStorageKey: GOOGLE_DRIVE_CONNECTION_STATUS_STORAGE_KEY,
		icon: <GoogleDriveIcon />,
		tab: 'google_drive',
	},
	[ThirdPartyConnectBannerSource.ONE_DRIVE]: {
		fetchBannerStatusFromLocalStorage: getOneDriveConnectionBannerStatus,
		bannerStatusStorageKey: ONE_DRIVE_CONNECTION_BANNER_STORAGE_KEY,
		connectorCheckMethod: (cloudId: string) => checkIfUserHasAuthorizedOneDriveConnector(cloudId),
		fetchConnectionStatusFromLocalStorage: getOneDriveConnectionStatus,
		connectionStatusStorageKey: ONE_DRIVE_CONNECTION_STATUS_STORAGE_KEY,
		icon: <OneDriveIcon />,
		tab: 'onedrive',
	},
};

interface ThirdPartyConnectBannerProps {
	cloudId: string;
	source: ThirdPartyConnectBannerSource;
}

const ThirdPartyConnectBanner: React.FC<ThirdPartyConnectBannerProps> = ({ cloudId, source }) => {
	const {
		bannerStatusStorageKey,
		connectorCheckMethod,
		connectionStatusStorageKey,
		fetchBannerStatusFromLocalStorage,
		fetchConnectionStatusFromLocalStorage,
		icon,
		tab,
	} = ThirdPartySourceMapping[source];
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [integrationDetails, setIntegrationDetails] =
		useBrowserStorage<ThirdPartyConnectionStatus | null>(
			connectionStatusStorageKey,
			fetchConnectionStatusFromLocalStorage(),
		);
	const [bannerDismissState, setBannerDismissState] =
		useBrowserStorage<ThirdPartyBannerStatus | null>(
			bannerStatusStorageKey,
			fetchBannerStatusFromLocalStorage(),
		);

	const hasBannerBeenDismissed = bannerDismissState?.hasBannerBeenDismissed || false;
	const hasAuthorized = integrationDetails?.isUserOAuthed || false;
	const hasIntegrationDetails = Object.keys(integrationDetails || {}).length > 0;
	const hideBanner = hasAuthorized || hasBannerBeenDismissed || !hasIntegrationDetails;

	const trackEvent = (event: AnalyticsEventPayload) => {
		createAnalyticsEvent(event).fire(ANALYTICS_CHANNEL);
	};

	const handleBroadcastedOAuthSuccess = (_: Event) => {
		setBannerDismissState({ hasBannerBeenDismissed: true });
	};

	// If the user has authorized through the empty state UI, close the banner.
	useEffect(() => {
		window.addEventListener(THIRD_PARTY_OAUTH_SUCCESS_EVENT, handleBroadcastedOAuthSuccess);
		return () => {
			window.removeEventListener(THIRD_PARTY_OAUTH_SUCCESS_EVENT, handleBroadcastedOAuthSuccess);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleConnectionClick = () => {
		if (!integrationDetails || !integrationDetails.outboundAuthUrl) {
			return;
		}

		trackEvent(buildAuthClickedEventPayload(ThirdPartyAuthSource.CONNECT_BANNER, tab));

		triggerThirdPartyAuthFlow(integrationDetails.outboundAuthUrl)
			.then(() => {
				setIntegrationDetails({
					...integrationDetails,
					isUserOAuthed: true,
					expirationDate: Date.now() + DEFAULT_EXPIRATION_FOR_3P_CONNECTION_STATUS,
				});
				trackEvent(buildAuthCompletedEventPayload(ThirdPartyAuthSource.CONNECT_BANNER, tab));
			})
			.catch((_) => {
				trackEvent(buildAuthFailedEventPayload(ThirdPartyAuthSource.CONNECT_BANNER, tab));
			});
	};

	// Fetch connection status if it hasn't been fetched yet and the banner hasn't been dismissed
	useEffect(() => {
		const fetchThirdPartyConnectionStatus = async () => {
			await connectorCheckMethod(cloudId);
			setIntegrationDetails(fetchConnectionStatusFromLocalStorage());
		};
		if (!integrationDetails && !hasBannerBeenDismissed) {
			fetchThirdPartyConnectionStatus();
		}
	}, [
		cloudId,
		integrationDetails,
		hasBannerBeenDismissed,
		setIntegrationDetails,
		connectorCheckMethod,
		fetchConnectionStatusFromLocalStorage,
	]);

	// If the user has authorized or the banner has been dismissed, don't render the banner
	if (hideBanner) {
		return null;
	}

	return (
		<Grid alignItems="center" xcss={cx(styles.containerBaseStyles)}>
			{icon}
			<Inline alignBlock="center" xcss={styles.pressableInlineStyles}>
				<Pressable xcss={styles.pressableStyles} onClick={handleConnectionClick}>
					{formatMessage(messages.thirdPartyConnectBannerLink)}
				</Pressable>
			</Inline>
			<Inline alignBlock="center" xcss={styles.closeButtonContainer}>
				<IconButton
					appearance="subtle"
					label={formatMessage(messages.thirdPartyConnectBannerClose)}
					spacing="compact"
					icon={() => <CrossIcon label="Close" size="medium" />}
					onClick={() => setBannerDismissState({ hasBannerBeenDismissed: true })}
					testId={`${tab}-connect-banner-close-button`}
				/>
			</Inline>
		</Grid>
	);
};

const ThirdPartyConnectBannerWithAnalytics = withAnalyticsContext(ThirdPartyConnectBanner);

export const renderGoogleDriveConnectBanner = (cloudId: string | null) => {
	if (!cloudId) {
		return null;
	}
	return (
		<ThirdPartyConnectBannerWithAnalytics
			cloudId={cloudId}
			source={ThirdPartyConnectBannerSource.GOOGLE_DRIVE}
		/>
	);
};

export const renderOneDriveConnectBanner = (cloudId: string | null) => {
	if (!cloudId) {
		return null;
	}
	return (
		<ThirdPartyConnectBannerWithAnalytics
			cloudId={cloudId}
			source={ThirdPartyConnectBannerSource.ONE_DRIVE}
		/>
	);
};
